import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Typography
} from '@mui/material';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import { DataContext } from '../context/DataContext';
import SemanticWorkspaceService from '../services/SemanticWorkspaceService';

/**
 * WorkspaceSelector Component
 * 
 * Displays a dropdown menu for selecting workspaces. Fetches available workspaces
 * for the authenticated user and manages workspace selection through DataContext.
 * 
 * @param {Object} props
 * @param {Function} props.onWorkspaceChange - Optional callback invoked when workspace changes
 */
const WorkspaceSelector = ({ onWorkspaceChange }) => {
  const { accountId, authResolved, selectedWorkspace, updateSelectedWorkspace, isPlatformRoot, isTenantAdmin } = useContext(DataContext);
  const navigate = useNavigate();

  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch workspaces on component mount
  useEffect(() => {
    let isMounted = true;

    const fetchWorkspaces = async () => {
      if (!accountId) {
        if (isMounted) {
          // Mientras la sesión de Cognito todavía se está resolviendo
          // (`authResolved === false`) NO es un error: el `accountId` aún
          // puede llegar. Nos quedamos en estado de carga y este efecto se
          // vuelve a disparar cuando `accountId` cambie. Solo lo tratamos
          // como error si la auth ya terminó y aun así no hay cuenta.
          if (authResolved) {
            setLoading(false);
            setError('No se pudo obtener el ID de cuenta');
          } else {
            setLoading(true);
            setError(null);
          }
        }
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await SemanticWorkspaceService.listByAccount(accountId);
        
        if (!isMounted) return;

        const workspaceList = response?.items || [];
        setWorkspaces(workspaceList);

        // Handle default workspace selection
        if (workspaceList.length > 0) {
          // Check if cached workspace exists in current list
          if (selectedWorkspace) {
            const cachedWorkspaceExists = workspaceList.some(
              (ws) => ws.semantic_workspace_id === selectedWorkspace.semantic_workspace_id
            );

            if (!cachedWorkspaceExists) {
              // Cached workspace no longer available, select first workspace
              console.warn(
                `⚠️ Workspace "${selectedWorkspace.name}" no longer available. ` +
                `Selecting first available workspace.`
              );
              const firstWorkspace = workspaceList[0];
              updateSelectedWorkspace(firstWorkspace);
              
              if (onWorkspaceChange) {
                onWorkspaceChange(firstWorkspace);
              }
            }
          } else {
            // No workspace selected, select first one
            const firstWorkspace = workspaceList[0];
            updateSelectedWorkspace(firstWorkspace);
            
            if (onWorkspaceChange) {
              onWorkspaceChange(firstWorkspace);
            }
          }
        } else {
          // No workspaces available
          updateSelectedWorkspace(null);
        }
      } catch (err) {
        if (!isMounted) return;
        
        console.error('Error fetching workspaces:', err);
        setError('No se pudo cargar los workspaces. Por favor intenta de nuevo.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchWorkspaces();

    return () => {
      isMounted = false;
    };
  }, [accountId, authResolved]); // Re-evaluar cuando la cuenta o el estado de auth cambie

  // Handle workspace selection change
  const handleChange = (event) => {
    const workspaceId = event.target.value;
    const workspace = workspaces.find((ws) => ws.semantic_workspace_id === workspaceId);
    
    if (workspace) {
      updateSelectedWorkspace(workspace);
      
      if (onWorkspaceChange) {
        onWorkspaceChange(workspace);
      }
    }
  };

  // Render loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={20} sx={{ color: 'var(--wb-primary)' }} />
        <Typography variant="body2" color="var(--wb-text-soft)">
          Cargando workspaces...
        </Typography>
      </Box>
    );
  }

  // Render error state
  if (error) {
    return (
      <Alert severity="error" sx={{ py: 0.5 }}>
        {error}
      </Alert>
    );
  }

  // Render empty state
  if (workspaces.length === 0) {
    const canConnectData = isPlatformRoot || isTenantAdmin;

    return (
      <Alert
        severity="info"
        sx={{ py: 0.5 }}
        action={
          canConnectData ? (
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate('/admin/home/semantic-models/conexiones')}
            >
              Conectar datos
            </Button>
          ) : undefined
        }
      >
        {canConnectData
          ? 'No hay workspaces disponibles. Conecta una fuente de datos para empezar.'
          : 'No hay workspaces disponibles. Contacta al administrador.'}
      </Alert>
    );
  }

  // Render workspace selector
  return (
    <FormControl fullWidth size="small">
      <InputLabel 
        id="workspace-selector-label"
        sx={{ 
          color: 'var(--wb-text-faint)',
          '&.Mui-focused': { color: 'var(--wb-primary)' }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <WorkspacesIcon sx={{ fontSize: 18 }} />
          Workspace
        </Box>
      </InputLabel>
      <Select
        labelId="workspace-selector-label"
        id="workspace-selector"
        value={selectedWorkspace?.semantic_workspace_id || ''}
        onChange={handleChange}
        label="Workspace"
        sx={{
          color: 'var(--wb-text-strong)',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--wb-outline)'
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--wb-text-ghost)'
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'var(--wb-primary)'
          },
          '& .MuiSvgIcon-root': {
            color: 'var(--wb-text-faint)'
          }
        }}
      >
        {workspaces.map((workspace) => (
          <MenuItem
            key={workspace.semantic_workspace_id}
            value={workspace.semantic_workspace_id}
          >
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                {workspace.name || workspace.workspace_name || 'Sin nombre'}
              </Typography>
              {workspace.description && (
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.25 }}
                >
                  {workspace.description}
                </Typography>
              )}
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default WorkspaceSelector;
